import Badge from '@/app/components/common/Badge'
import Button from '@/app/components/common/Button'
import Pill from '@/app/components/common/Pill'
import Text from '@/app/components/common/Text'
import Title from '@/app/components/common/Title'
import HotelCardCarousel from '@/app/components/HotelCardCarousel'
import HotelListItemClick from '@/app/components/HotelListItemClick'
import { Icons } from '@/app/components/ui/Icons'
import {
	DEFAULT_USER_COUNTRY,
	INIT_DATA_STORAGE_KEY,
	KM_TO_MILES,
	SM_BREAKPOINT,
	UUID_NAMESPACE,
} from '@/app/constants'
import '@/app/styles/components/HotelListItem.css'
import { HotelItem, HotelsListResponse, UserData } from '@/app/types'
import {
	getRatingColor,
	getRatingValue,
	isApartment,
	isFromUS,
} from '@/app/utils'
import { useLocalStorage, useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { FC, Fragment, PropsWithChildren, useState } from 'react'
import { v5 as uuid } from 'uuid'
import Stars from './Stars'

const MAX_PRICE_LENGTH = 7

export interface HotelListItemProps extends PropsWithChildren {
	item: HotelItem
	index: number
	className?: string
	lang?: string
	isSingleHotel?: boolean
	isLandmark?: boolean
	searchParams: HotelsListResponse['searchParams']
}

const HotelListItem: FC<HotelListItemProps> = (props) => {
	const {
		item,
		index,
		lang,
		isSingleHotel,
		searchParams,
		isLandmark,
		className,
		children,
	} = props
	const t = useTranslations('Keywords')
	const hasAvailability = item.rawHotelData.bestRate.availableRooms > 0
	const isArabic = lang === 'ar'
	const [isRatesExpanded, setIsRatesExpanded] = useState(false)
	const isMobile = useMediaQuery(SM_BREAKPOINT)

	const [userData, _] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)
	const userCountry = userData?.userCountry || DEFAULT_USER_COUNTRY
	const slides = [
		item.largeImage || item.image || '',
		...(item.rawHotelData.moreImages
			?.slice(0, 4)
			.map((image) => image.small || image.large || '') || []),
	].filter(Boolean)

	const toggleRates = () => {
		setIsRatesExpanded(!isRatesExpanded)
	}

	const renderRates = () => {
		const rates = item.rates.slice(0, 3) // Show maximum 3 rates

		return rates.map((rate, i) => (
			<Fragment key={rate.bookURI + i}>
				<div
					className={classNames('hotel-list-item__rate', {
						'md:col-start-3': rates.length === 1,
						'md:col-start-2': rates.length === 2 && i === 0,
					})}
				>
					<HotelListItemClick
						passthruLink={`/${lang}/passthru?uid=${uuid(
							rate.bookURI,
							UUID_NAMESPACE
						)}`}
						link={rate.bookURI}
						provider={rate.providerName}
						item={item}
						searchParams={searchParams}
						isAdditionalPrice
						price={rate.rawPrice}
					/>
					<Image
						loading={'lazy'}
						width={60}
						height={12}
						style={{ height: 'auto' }}
						src={rate.provider.logo}
						alt={`${rate.provider.logo} image`}
						unoptimized
						className="md:mx-auto"
					/>
					<Text color={null} font={null} className="text-xs">
						{rate.nightlyRate}
					</Text>
				</div>
				{i !== rates.length - 1 && (
					<>
						<div className="max-md:hidden h-[30px] w-[1px] bg-text-200"></div>
					</>
				)}
			</Fragment>
		))
	}

	const renderRating = () => {
		const { rating, ratingText, numberOfReviews } = item
		return (
			!!rating && (
				<div className="hotel-list-item__rating">
					<>
						<div
							className="hotel-list-item__rating-badge max-md:hidden"
							data-test-id="hotel-list-item__rating"
						>
							{/* desktop rating */}
							<Badge bgColor={getRatingColor(rating)} textColor="white">
								{getRatingValue(rating)}
							</Badge>
						</div>
						<div
							className={`text-${getRatingColor(
								rating
							)} font-semibold text-sm max-md:me-1 md:hidden`}
						>
							{getRatingValue(rating)}
						</div>
					</>
					{ratingText && (
						<div className="hotel-list-item__rating-text">
							<Text
								tag="div"
								font="semibold"
								size="sm"
								className={classNames(
									'leading-5 order-2 md:order-1 ',
									`text-${getRatingColor(rating)}`
								)}
							>
								{ratingText}
							</Text>
							{numberOfReviews > 0 && (
								<Text
									size="xs"
									className="text-text-600 order-3 md:order-2 max-md:ms-1"
								>
									({numberOfReviews} {t('reviews')})
								</Text>
							)}
						</div>
					)}
				</div>
			)
		)
	}

	const distanceInKm = Number((item.distanceFromCenter / 1000).toFixed(1))

	const getDistanceFromCenter = () => {
		if (distanceInKm < 1) {
			return t('inCityCenter')
		}

		const newDistance = distanceInKm - 1
		if (newDistance < 0.2) {
			return t('inCityCenter')
		}
		const isUS = isFromUS(userCountry)
		const unit = isUS ? 'mi' : 'km'
		const convertedDistance = isUS ? newDistance * KM_TO_MILES : newDistance

		return `${Number(convertedDistance).toFixed(1)} ${unit} ${t(
			'fromTheCenter'
		)}`
	}

	const renderLocation = () => {
		const { location, distanceFromCenter } = item
		const locationInfo = location || distanceFromCenter

		if (!locationInfo) return null

		return (
			<div className="hotel-list-item__location">
				{location && (
					<div className="hotel-list-item__location-icon">
						<Icons.iconPin stroke="#334155" />
					</div>
				)}

				<div className="hotel-list-item__location-content">
					<Text size="xs" color={'text-800'}>
						<span>{location}</span>
						{!isLandmark && (
							<span>
								{location && ' · '}
								{getDistanceFromCenter()}
							</span>
						)}
					</Text>
				</div>
			</div>
		)
	}

	const renderRedTexts = () => {
		const rooms = item.rawHotelData.bestRate.availableRooms
		const type = item.propertyType

		// Early return if more than 5 rooms
		if (rooms > 5) {
			return null
		}

		// For 2-5 rooms
		if (rooms >= 2) {
			return (
				<Pill color="error" background="error-100">
					{t('onlyRoomsLeft', { count: rooms })}
				</Pill>
			)
		}

		// For 1 room (only for hotels, not apartments)
		if (rooms === 1 && !isApartment(type)) {
			return (
				<Pill color="error" background="error-100">
					{t('oneRoomLeft')}
				</Pill>
			)
		}

		return null
	}

	const renderGreenTexts = () => {
		const data = item.rawHotelData.bestRate
		const { hasFreeCancellation, canPayLater, availableRooms } = data
		const renderRoomsLeft = availableRooms > 5 && availableRooms <= 7

		return (
			<div className="flex flex-wrap gap-2">
				{renderRoomsLeft && (
					<Pill color="success-950" background="success-100">
						{t('roomsLeft', { count: availableRooms })}
					</Pill>
				)}
				{hasFreeCancellation && (
					<Pill color="success-950" background="success-100">
						{t('freeCancellation')}
					</Pill>
				)}
				{canPayLater && (
					<Pill color="success-950" background="success-100">
						{t('noPrepayment')}
					</Pill>
				)}
			</div>
		)
	}
	const MAX_TITLE_LENGTH = 60

	const renderTitle = () => {
		const titleToRender = isArabic
			? item.translatedName || item.title
			: item.title
		const isTitleTooLong = titleToRender.length > MAX_TITLE_LENGTH

		return (
			<div className="flex lg:justify-between max-lg:flex-col-reverse">
				<Title
					tag="h3"
					level="3"
					bold
					fontSize="2xl"
					data-test-id="hotel-list-item__title"
					className={classNames('hotel-list-item__title', {
						'hotel-list-item__title--arabic':
							isArabic && item.translatedName === item.title,
					})}
					title={isTitleTooLong ? titleToRender : undefined} // Show full title on hover if truncated
				>
					{isTitleTooLong
						? `${titleToRender.substring(0, MAX_TITLE_LENGTH).trim()}...`
						: titleToRender}
				</Title>
				<Stars count={item.stars} className="flex items-center max-lg:mb-1 " />
			</div>
		)
	}

	const renderBestPriceOn = () =>
		item.rawHotelData.bestRate.provider.logo ? (
			<>
				<Text size="xs" className="me-1.5" color="text-700">
					{t('bestPriceOnLogo')}
				</Text>
				<Image
					loading={'lazy'}
					width={60}
					height={16}
					src={item.rawHotelData.bestRate.provider.logo}
					alt={`${item.rawHotelData.bestRate.provider.logo} image`}
					quality={100}
					className="hotel-list-item__best-price-logo"
					style={{ objectFit: 'contain' }}
				/>
			</>
		) : null

	const renderMdViewDeal = () => (
		<div className="hotel-list-item__view-deal">
			{renderRating()}

			{hasAvailability && (
				<>
					<div data-test-id="price">
						<Text size="2xl" className="hotel-list-item__price" font="bold">
							{item.price}
						</Text>
						<Text size="xs">/{t('perNight')}</Text>
						<Text tag="p" size="xs" color="text-700" className="mt-2">
							{t('taxes')}
						</Text>
					</div>
				</>
			)}
			<div className="hotel-list-item__best-price">{renderBestPriceOn()}</div>
			<HotelListItemClick
				passthruLink={`/${lang}/passthru?uid=${uuid(
					item.link,
					UUID_NAMESPACE
				)}`}
				link={item.link}
				provider={item.provider}
				item={item}
				searchParams={searchParams}
				price={item.rawPrice}
			/>
			<Button
				bgColor="main"
				textColor="white"
				fontSize="base"
				name="View Deal"
				className={classNames('hotel-list-item__view-deal-button', {
					'p-0': isSingleHotel,
				})}
				data-testid="hotel-list-item__view-deal-button"
			>
				{hasAvailability ? (
					t('viewDeal')
				) : (
					<span className="flex items-center justify-center w-full">
						{t('seeAvailability')}
						<Icons.iconArrowSmall className="ms-2" />
					</span>
				)}
			</Button>
		</div>
	)

	const renderSmViewDeal = () => {
		const isPriceToLong = item.price.length > MAX_PRICE_LENGTH

		return (
			<div className="hotel-list-item__sm-view-deal-and-rates">
				<div className="hotel-list-item__sm-view-deal">
					<div className="w-full h-[1px] bg-text-200 mb-3"></div>
					<HotelListItemClick
						passthruLink={`/${lang}/passthru?uid=${uuid(
							item.link,
							UUID_NAMESPACE
						)}`}
						link={item.link}
						provider={item.provider}
						item={item}
						searchParams={searchParams}
						price={item.rawPrice}
					/>

					<div
						className={classNames('hotel-list-item__sm-price-container', {
							'hotel-list-item__price-container--long': isPriceToLong,
						})}
					>
						{hasAvailability && (
							<>
								<div className="flex flex-col">
									<div className="flex justify-start items-center">
										<Text
											tag="div"
											size="xl"
											font="bold"
											className="hotel-list-item__sm-price"
										>
											{item.price}
										</Text>
										<Text className="hotel-list-item__per-night">
											/{t('perNight')}
										</Text>
									</div>
									<Text tag="p" size="xs" color="text-700" className="mt-1">
										{t('taxes')}
									</Text>
									<div className="hotel-list-item__best-price">
										{renderBestPriceOn()}
									</div>
								</div>
								<div>
									<Button
										bgColor="main"
										textColor="white"
										name="View Deal"
										className="hotel-list-item__view-deal-button relative z-10 pointer-events-none"
									>
										{t('viewDeal')}
									</Button>
								</div>
							</>
						)}
					</div>
				</div>
				{item.rates.length > 0 && (
					<div className="hotel-list-item__sm-rates">
						<div
							className="text-sm font-bold flex justify-center items-center pb-2"
							onClick={toggleRates}
						>
							{isRatesExpanded ? t('hide') : t('morePrices')}
							<Icons.caret
								className={classNames('ms-2 transition-transform', {
									'rotate-180': isRatesExpanded,
								})}
							/>
						</div>
						<AnimatePresence>
							{isRatesExpanded && (
								<motion.div
									initial={{ height: 0, opacity: 0 }}
									animate={{ height: 'auto', opacity: 1 }}
									exit={{ height: 0, opacity: 0 }}
									transition={{ duration: 0.2 }}
									className="overflow-hidden"
								>
									{renderRates()}
								</motion.div>
							)}
						</AnimatePresence>
					</div>
				)}
			</div>
		)
	}

	return (
		<div
			className={classNames('hotel-list-item', className, {
				'mt-0': isSingleHotel || index === 0,
			})}
			data-test-id="hotel-item"
		>
			<div className="hotel-list-item__inner">
				{/* image */}
				<div className="hotel-list-item__images-container">
					<HotelCardCarousel slides={slides} />
				</div>

				{/* content */}
				<div className="hotel-list-item__content">
					<div className="hotel-list-item__main-content">
						{/* header */}
						<div className="hotel-list-item__header">
							{renderTitle()}
							<div className="hotel-list-item__location-wrapper">
								{renderLocation()}
							</div>
							<div className="hotel-list-item__rating--mobile">
								{renderRating()}
							</div>
							{/* notice */}
							<div className="hotel-list-item__notices">
								{renderRedTexts()}
								{renderGreenTexts()}
							</div>
						</div>
						{hasAvailability ? (
							/* rates */
							<>
								{renderSmViewDeal()}
								{item.rates.length > 0 && (
									<div className="hotel-list-item__rates">{renderRates()} </div>
								)}
							</>
						) : (
							<div className="max-md:mb-4 relative">
								{isMobile && (
									<div className="w-full h-[1px] bg-text-200 mb-3"></div>
								)}

								<div
									className="flex justify-start items-center text-text-600 text-xs md:pb-1 max-md:mb-3"
									data-test-id="no-room-available"
								>
									{!isMobile && <Icons.iconAlertSmall className="me-2" />}
									<span>{t('singleHotelNotFound')}</span>
								</div>
								{isMobile && (
									<>
										<HotelListItemClick
											passthruLink={`/${lang}/passthru?uid=${uuid(
												item.link,
												UUID_NAMESPACE
											)}`}
											link={item.link}
											provider={item.provider}
											item={item}
											searchParams={searchParams}
											price={item.rawPrice}
										/>

										<Button
											bgColor="main"
											textColor="white"
											fontSize="base"
											name="View Deal"
											className="hotel-list-item__view-deal-button !z-0"
										>
											<span className="flex items-center justify-center w-full">
												{t('seeAvailability')}
												<Icons.iconArrowSmall className="ms-2" />
											</span>
										</Button>
									</>
								)}
							</div>
						)}
					</div>
					{renderMdViewDeal()}
				</div>
			</div>
			{children}
		</div>
	)
}

export default HotelListItem
